import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { SITE, getEmailLink, getWhatsAppLink } from "@/config/site";

const NAV = [
  { to: "/", label: "Início" },
  { to: "/servicos", label: "Serviços" },
  { to: "/sobre", label: "Sobre" },
  { to: "/contato", label: "Contato" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [surfaceTheme, setSurfaceTheme] = useState("dark");
  const headerRef = useRef(null);
  const location = useLocation();
  const emailLink = getEmailLink();
  const whatsappLink = getWhatsAppLink();
  const compact = scrolled || location.pathname !== "/" || open;

  useLayoutEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    let frame;

    const updateHeader = () => {
      setScrolled(window.scrollY > 48);

      if (open) {
        setSurfaceTheme("dark");
        return;
      }

      const surface = document
        .elementsFromPoint(window.innerWidth / 2, 36)
        .map((element) => element.closest?.("[data-header-theme]"))
        .find((element) => element && !headerRef.current?.contains(element));

      setSurfaceTheme(surface?.dataset.headerTheme || "dark");
    };

    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateHeader);
    };

    scheduleUpdate();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [location.pathname, open]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const darkInk = compact && surfaceTheme === "light" && !open;

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,backdrop-filter] duration-500 ${compact ? darkInk ? "border-black/10 bg-white/20 backdrop-blur-2xl" : "border-white/10 bg-black/20 backdrop-blur-2xl" : "border-transparent bg-transparent"}`}
        data-testid="site-header"
      >
        <div className={`site-shell relative flex items-center transition-[height] duration-500 ${compact ? "h-[72px]" : "h-[88px]"}`}>
          <div className={`absolute left-0 hidden transition-all duration-500 lg:block ${compact ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-3 opacity-0"}`}>
            <Link to="/contato" className={`group inline-flex h-11 items-center gap-3 rounded-full pl-5 pr-1.5 text-xs font-bold transition-colors ${darkInk ? "bg-[#050606] text-white hover:bg-black/75" : "bg-[#f1f3ec] text-[#050606] hover:bg-lime-300"}`} data-testid="header-compact-cta">
              Iniciar projeto
              <span className={`flex h-8 w-8 items-center justify-center rounded-full ${darkInk ? "bg-lime-300 text-[#050606]" : "bg-[#050606] text-white"}`}>
                <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          </div>

          <Link
            to="/"
            className={`relative z-10 shrink-0 outline-none transition-opacity focus:outline-none focus-visible:opacity-65 lg:absolute lg:transition-all lg:duration-500 ${compact ? "lg:left-1/2 lg:-translate-x-1/2" : "lg:left-0 lg:translate-x-0"}`}
            data-testid="header-logo"
            aria-label="Clube Digital, início"
          >
            <span className={`block whitespace-nowrap font-sans leading-none tracking-normal transition-[font-size,color] duration-300 ${compact ? "text-[22px]" : "text-[25px]"} ${darkInk ? "text-[#050606]" : "text-white"}`}>
              <strong className="font-extrabold">Clube</strong> <span className="font-normal">Digital</span>
            </span>
          </Link>

          <nav className={`absolute left-1/2 hidden -translate-x-1/2 items-center gap-9 transition-all duration-500 lg:flex ${compact ? "pointer-events-none -translate-y-3 opacity-0" : "translate-y-0 opacity-100"}`} aria-label="Navegação principal">
            {NAV.slice(1).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `relative py-3 text-[11px] font-bold uppercase tracking-[.15em] transition-colors ${isActive ? "text-lime-300" : "text-white/62 hover:text-white"}`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center">
            <Link to="/contato" className={`group hidden h-12 items-center gap-4 rounded-full bg-[#f1f3ec] pl-6 pr-1.5 text-sm font-bold text-[#050606] transition-all duration-500 lg:inline-flex ${compact ? "pointer-events-none translate-y-3 opacity-0" : "translate-y-0 opacity-100"}`} data-testid="header-cta">
              Vamos conversar
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#050606] text-white">
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>

            <button
              type="button"
              onClick={() => setOpen((value) => !value)}
              className={`group flex items-center focus:outline-none focus-visible:text-lime-300 lg:absolute lg:right-0 lg:transition-all lg:duration-500 ${darkInk ? "text-[#050606]" : "text-white"} ${compact ? "lg:translate-y-0 lg:opacity-100" : "lg:pointer-events-none lg:-translate-y-3 lg:opacity-0"}`}
              aria-label={open ? "Fechar menu" : "Abrir menu"}
              aria-expanded={open}
              aria-controls="site-menu"
              data-testid="menu-toggle"
            >
              <span className={`mr-3 hidden text-[10px] font-bold uppercase tracking-[.18em] transition-colors lg:block ${darkInk ? "text-black/55 group-hover:text-black" : "text-white/55 group-hover:text-white"}`}>{open ? "Fechar" : "Menu"}</span>
              <span className={`flex h-11 w-11 items-center justify-center rounded-full border backdrop-blur-md transition-colors group-hover:border-lime-300 ${darkInk ? "border-black/20 bg-black/[.04] group-hover:text-black" : "border-white/20 bg-white/[.04] group-hover:text-lime-300"}`}>
                {open ? <X size={19} /> : <Menu size={19} />}
              </span>
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="site-menu"
            initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
            exit={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: .55, ease: [0.16, 1, 0.3, 1] }}
            className="editorial-grid fixed inset-0 z-40 overflow-y-auto bg-[#050606] pt-[96px] text-white"
            data-testid="site-menu"
          >
            <div className="site-shell grid min-h-[calc(100svh-96px)] gap-12 pb-10 lg:grid-cols-[1.35fr_.65fr]">
              <nav className="self-center" aria-label="Menu principal">
                {NAV.map((item, index) => (
                  <motion.div key={item.to} initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .55, delay: .12 + index * .06 }}>
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) => `group flex items-center justify-between border-b border-white/12 py-4 transition-colors hover:text-lime-300 sm:py-5 ${isActive ? "text-lime-300" : "text-white"}`}
                    >
                      <span className="display-title text-[clamp(2.7rem,6vw,6rem)] leading-none">{item.label}</span>
                      <span className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-[.18em] text-white/30">
                        0{index + 1}<ArrowUpRight size={22} className="text-white/55 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-lime-300" />
                      </span>
                    </NavLink>
                  </motion.div>
                ))}
              </nav>

              <motion.aside initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .6, delay: .34 }} className="flex flex-col justify-center border-t border-white/12 pt-8 lg:border-l lg:border-t-0 lg:pl-12 lg:pt-0">
                <p className="eyebrow">Contato direto</p>
                <p className="display-title mt-7 max-w-sm text-3xl leading-tight sm:text-4xl">Tem um projeto em mente? Vamos entender o próximo passo.</p>
                <div className="mt-9 space-y-3 text-sm">
                  {emailLink && <a href={emailLink} className="block text-white/60 transition-colors hover:text-lime-300">{SITE.email}</a>}
                  {whatsappLink && <a href={whatsappLink} target="_blank" rel="noreferrer" className="block text-white/60 transition-colors hover:text-lime-300">{SITE.phoneDisplay}</a>}
                </div>
                <Link to="/contato" onClick={() => setOpen(false)} className="group mt-9 inline-flex min-h-14 items-center justify-between rounded-md bg-lime-300 px-5 text-sm font-bold text-[#050606]">
                  Iniciar um projeto<ArrowUpRight size={20} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              </motion.aside>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
